import React, { useCallback, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import classNames from 'classnames';

import { flat, FLAT_SELECT_CLASS } from './reconciliationSelectStyles';
import { COMMITMENT_APPROVED, COMMITMENT_CANCELED, COMMITMENT_REJECTED, rowState } from './sendForCommitmentHelpers';

import { formatDateWithTz } from 'Helpers/dateUtils';
import { formatFigure, formatToFixedMoneyWithLocale } from 'Helpers/formatService';

import Badge from 'CommonComponents/Badge';
import CheckboxInput from 'CommonComponents/CheckboxInput';
import TextButton from 'CommonComponents/TextButton';
import UserSelectorInput from 'CommonComponents/UserSelectorInput';

const DASH = '—';

/**
 * The commit price, which is the budgeted price and is not editable here.
 *
 * The explanation is portalled to `document.body` and positioned `fixed` from the
 * trigger's own rect. A tooltip rendered in place is clipped by the table's scroll
 * container, so on the lower rows — the ones a reconciler reaches last — it would be
 * invisible exactly when it is needed.
 *
 * Measured on open rather than on mount: the row may have scrolled since, and the
 * rect is only true at the moment it is read. `getBoundingClientRect` is viewport-
 * relative, which is what `fixed` wants, and it sidesteps `offsetParent` — that
 * reads null inside fixed ancestors.
 */
function LockedPrice({ value }) {
    const triggerRef = useRef(null);
    const [rect, setRect] = useState(null);

    const show = useCallback(() => {
        const node = triggerRef.current;

        if (node) {
            setRect(node.getBoundingClientRect());
        }
    }, []);

    const hide = useCallback(() => setRect(null), []);

    return (
        <>
            <span
                ref={triggerRef}
                className="input input--disabled text-input !tw-w-20 tw-text-end tw-inline-flex tw-items-center tw-justify-end tw-cursor-help"
                tabIndex={0}
                role="note"
                aria-label={$t('Commit price, locked to the budgeted price')}
                onMouseEnter={show}
                onMouseLeave={hide}
                onFocus={show}
                onBlur={hide}
            >
                {value === null || value === undefined ? DASH : formatFigure(value, formatToFixedMoneyWithLocale, { precision: 2 })}
            </span>
            {rect &&
                ReactDOM.createPortal(
                    <div
                        role="tooltip"
                        className="tw-fixed tw-z-50 tw-max-w-xs tw-rounded tw-bg-charcoal tw-px-2 tw-py-1 tw-text-xs tw-text-white tw-shadow-primary"
                        style={{ top: rect.bottom + 6, left: rect.left }}
                    >
                        {$t('The commit price is the budgeted price and cannot be changed here. To commit at a different price, edit the budget line first.')}
                    </div>,
                    document.body,
                )}
        </>
    );
}

/**
 * The commitment already raised from this row, where the row carries one.
 *
 * A rejected or cancelled order is still reported, even though it no longer blocks
 * the row: it is why the row is back in front of the reconciler, and the readiness
 * badge beside it says the row can be sent again.
 */
function CommitmentBadge({ state }) {
    if (state.commitmentStatus === COMMITMENT_REJECTED) {
        return <Badge variant="danger">{$t('Commitment rejected')}</Badge>;
    }

    if (state.commitmentStatus === COMMITMENT_CANCELED) {
        return <Badge variant="neutral">{$t('Commitment cancelled')}</Badge>;
    }

    if (state.commitmentStatus === COMMITMENT_APPROVED) {
        return <Badge variant="success">{$t('Committed')}</Badge>;
    }

    if (state.submitted) {
        return <Badge variant="info">{$t('Awaiting approval')}</Badge>;
    }

    return null;
}

/**
 * The row's verdict, as a single badge.
 *
 * Only two of these are commit-value errors — below the actual, and above the
 * uncommitted balance. The commit value is no longer required to equal the actual,
 * so there is no "must match" verdict: a quantity that raises the commitment above
 * what was spent is the ordinary case this screen exists to allow.
 *
 * Ordered by what the reconciler can do about it: a line that cannot be procured at
 * all comes before any figure typed against it, and a row's own verdict comes before
 * the group's.
 */
function ReadinessBadge({ state, group }) {
    if (!state.procurable) {
        return <Badge variant="danger">{$t('Not procurable')}</Badge>;
    }

    if (state.belowActual) {
        return <Badge variant="danger">{$t('Below the actual value')}</Badge>;
    }

    if (state.aboveUncommitted) {
        return <Badge variant="warning">{$t('Above the uncommitted balance')}</Badge>;
    }

    if (state.balanceUnapplied) {
        return <Badge variant="warning">{$t('Cashflow needs the balance applied')}</Badge>;
    }

    if (group?.exceeds) {
        return <Badge variant="warning">{$t('Group exceeds budget line')}</Badge>;
    }

    if (state.cashflowStale) {
        return <Badge variant="danger">{$t('Cashflow no longer matches')}</Badge>;
    }

    return <Badge variant="success">{$t('Ready')}</Badge>;
}

/**
 * Whether this row is raised on its own or alongside the others it matches.
 *
 * The offer is only made on a SELECTED row with a selected match, because
 * combining is a decision about the submission being assembled rather than about
 * the queue at large.
 */
function CombineCell({ group, combinable, onCombine, onUngroup }) {
    if (group) {
        return (
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-y-0.5">
                <Badge variant="info">{$t('Group {{number}} ({{count}})', { number: group.number, count: group.size })}</Badge>
                <TextButton onClick={() => onUngroup(group.id)}>{$t('Ungroup')}</TextButton>
            </div>
        );
    }

    if (combinable) {
        return <TextButton onClick={onCombine}>{$t('Combine')}</TextButton>;
    }

    return <span className="tw-text-charcoal-50">{DASH}</span>;
}

/**
 * Raising the budget line the row overspends: the line's own edit page, which is
 * where it is done. Optional — the row commits either way — and worth offering
 * because topping the line up first avoids waiting on a budget change.
 *
 * A new tab on purpose. The queue holds the reconciler's selections, their typed
 * price/quantity splits, their combined groups and any adjusted cashflows, none of
 * it saved anywhere — navigating away in this tab would throw the lot away.
 *
 * Deliberately says nothing about what raising the line will do to this row. Where
 * the event requires approval for budget increases, saving the page raises a
 * request and leaves the line exactly as it is; where it does not, the line
 * changes at once. The queue cannot tell which from here, and only reloading it
 * will say.
 */
function EditBudgetLineLink({ url }) {
    return (
        <TextButton
            as="a"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            title={$t('Opens the budget line in a new tab. Reload the queue to pick up any change to it.')}
        >
            {$t('Edit budget line')}
        </TextButton>
    );
}

export default function SendForCommitmentRow({
    item,
    edit,
    selected,
    users,
    group,
    combinable,
    onToggleSelect,
    onEditChange,
    onOpenCashflow,
    onCombine,
    onUngroup,
}) {
    const state = rowState(item, edit);

    // A combined row does not draw on the budget line alone, so reporting its own
    // share here would understate what the commitment takes. Every row of a group
    // shows the same figure, which is what the line is actually left with.
    const uncommittedAfter = group ? group.uncommittedAfter : state.uncommittedAfter;

    return (
        <tr
            className={classNames({
                '!tw-bg-red-10': state.blocked || state.belowActual,
                '!tw-bg-blue-10': selected && !state.blocked && !state.belowActual,
            })}
        >
            <td className="tw-text-center">
                <CheckboxInput
                    checked={selected}
                    disabled={state.blocked}
                    onChange={() => onToggleSelect(item.id)}
                    aria-label={$t('Select this actual for commitment')}
                />
            </td>

            <td className="reconciliation-cell--truncate">
                <div className="tw-font-semibold">{item.supplier || DASH}</div>
                {!!item.description && <div className="tw-text-xs tw-text-charcoal-50">{item.description}</div>}
            </td>

            <td className="inbud-date-cell tw-whitespace-nowrap">{item.date ? formatDateWithTz(item.date, 'YYYY-MM-DD HH:mm:ss', 'DD/MM/YYYY') : DASH}</td>

            {/* The line, what it is worth and what is left of it, together: the quantity
                below is chosen against these two figures, so reading them should not mean
                looking across the table. */}
            <td className="reconciliation-cell--truncate">
                <div>{item.budget_line || DASH}</div>
                {item.budget_total !== null && item.budget_total !== undefined && (
                    <div className="tw-text-xs tw-text-charcoal-50">
                        {$t('Total {{total}} · {{uncommitted}} uncommitted', {
                            total: formatFigure(item.budget_total),
                            uncommitted: formatFigure(item.uncommitted),
                        })}
                    </div>
                )}
            </td>

            <td className="inbud-numeric-cell tw-font-semibold">{formatFigure(item.amount)}</td>

            <td>
                <div className="tw-flex tw-items-center tw-justify-end tw-gap-x-1">
                    <LockedPrice value={item.budgeted_price} />
                    <span className="tw-text-charcoal-25" aria-hidden="true">
                        ×
                    </span>
                    <input
                        type="number"
                        step="any"
                        className={classNames('input text-input !tw-w-20 tw-text-end', state.blocked ? 'input--disabled' : '!tw-bg-white')}
                        value={edit.quantity}
                        disabled={state.blocked}
                        aria-label={$t('Quantity')}
                        onChange={(e) => onEditChange(item.id, 'quantity', e.target.value)}
                    />
                </div>
            </td>

            {/* What the row actually commits. Its own column because it is no longer just
                the actual restated — the quantity moves it, and it is the figure every
                other number on the row is now judged against. */}
            <td className="inbud-numeric-cell tw-font-semibold">{formatFigure(state.committed)}</td>

            <td className="tw-whitespace-nowrap tw-text-center">
                {state.blocked ? (
                    <span className="tw-text-charcoal-50">{DASH}</span>
                ) : (
                    <TextButton onClick={() => onOpenCashflow(item.id)}>
                        {/* The count is the whole signal that a row has been touched: a
                            reconciler working a long queue needs to see which ones they
                            have already adjusted without reopening each. */}
                        {state.cashflowAdjusted ? $t('Cashflow ({{count}})', { count: state.cashflow.length }) : $t('Cashflow')}
                    </TextButton>
                )}
            </td>

            <td
                className={classNames('inbud-numeric-cell tw-font-semibold', {
                    'tw-text-red': uncommittedAfter !== null && uncommittedAfter < 0,
                    'tw-text-green': uncommittedAfter !== null && uncommittedAfter > 0,
                })}
                title={group ? $t('What the budget line has left once the whole group commits') : undefined}
            >
                {formatFigure(uncommittedAfter)}
            </td>

            <td className="tw-text-center">
                {state.procurable ? (
                    <span title={$t('Direct Commitment')}>
                        <Badge variant="info">{$t('DC')}</Badge>
                    </span>
                ) : (
                    <span className="tw-text-charcoal-50">{DASH}</span>
                )}
            </td>

            <td className="tw-text-center">
                <CheckboxInput
                    checked={edit.signed}
                    disabled={state.blocked}
                    onChange={(e) => onEditChange(item.id, 'signed', e.target.checked)}
                    aria-label={$t('Contract signed')}
                />
            </td>

            <td className="tw-min-w-[9rem]">
                <UserSelectorInput
                    selectedId={edit.ownerId}
                    usersList={users}
                    fetchIfNoUsers={false}
                    disabled={state.blocked}
                    className={classNames(FLAT_SELECT_CLASS, { 'input--disabled tw-opacity-60 tw-cursor-not-allowed': state.blocked })}
                    styles={flat}
                    aria-label={$t('Owner')}
                    onChange={(value) => onEditChange(item.id, 'ownerId', value)}
                />
            </td>

            <td>
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-1">
                    <CommitmentBadge state={state} />
                    {/* A settled commitment answers the row on its own. A returned one
                        does not: the row is live again, so it still reports whether it
                        is ready to go back. */}
                    {!state.submitted && <ReadinessBadge state={state} group={group} />}
                </div>
                {/* The badge names the condition; this names the consequence. Committing
                    over a line does not fail — it raises the line — and that is the part
                    the reconciler has to agree to. The submit confirmation says it again
                    with the totals; this is so the row is not silent about it.

                    Deliberately does not promise which route runs: that turns on the
                    approval settings for the line's own department or cost centre, which
                    the row does not carry. */}
                {state.aboveUncommitted && !state.submitted && (
                    <div className="tw-mt-1 tw-max-w-[16rem] tw-whitespace-normal tw-text-xs tw-text-charcoal-75">
                        {$t('Submitting raises this budget line, automatically or through the budget change process.')}
                    </div>
                )}
                {/* Beside the shortfall it answers, rather than in an actions column: it is
                    now the route to more headroom AND the only route to a different price,
                    so it belongs with the message that sends the reconciler there. Only
                    where the server said this user may actually edit the line. */}
                {state.editableShortfall && item.budget_line_edit_url && (
                    <div className="tw-mt-1">
                        <EditBudgetLineLink url={item.budget_line_edit_url} />
                    </div>
                )}
                {/* The approver's own words, which the badge cannot carry. Shown only
                    where one was written — the comment is optional on the rejection
                    form, so a row can be genuinely rejected with nothing to quote. */}
                {!!item.commitment?.rejection_comment && (
                    <div
                        className="tw-mt-1 tw-max-w-[16rem] tw-whitespace-normal tw-text-xs tw-italic tw-text-charcoal-75"
                        title={item.commitment.rejection_comment}
                    >
                        {item.commitment.rejection_comment}
                    </div>
                )}
            </td>

            <td className="tw-whitespace-nowrap tw-text-center">
                <CombineCell group={group} combinable={combinable} onCombine={() => onCombine(item.id)} onUngroup={onUngroup} />
            </td>
        </tr>
    );
}
